import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu } from "lucide-react";
import { scrollToTop } from "@/lib/scroll";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const navLinks = [
  { href: "/security", label: "Security" },
  { href: "/inheritance", label: "Inheritance" },
  { href: "/how-it-works", label: "How It Works" },
  { href: "/pricing", label: "Pricing" },
  { href: "/advisers", label: "Professionals" },
  { href: "/insights", label: "Insights" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

export default function SiteHeader() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === "/") return location === "/";
    return location.startsWith(href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-sm border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link href="/">
            <div onClick={() => scrollToTop()} className="flex items-center gap-2 cursor-pointer" data-testid="header-logo">
              <img 
                src="/assets/images/bitzo-logo.svg" 
                alt="Bitzo" 
                width={120} 
                height={32} 
                className="h-8 w-auto" 
              />
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-5" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <span
                  onClick={() => scrollToTop()}
                  className={`text-sm font-medium transition cursor-pointer ${
                    isActive(link.href)
                      ? "text-teal-400"
                      : "text-slate-400 hover:text-white"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </span>
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2.5">
            <Link href="/client/login">
              <Button variant="outline" className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700 hover:border-teal-500/60" data-testid="button-client-login">
                Client login
              </Button>
            </Link>
            <Link href="/partner/login">
              <Button variant="outline" className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700 hover:border-teal-500/60" data-testid="button-partner-login">
                Partner login
              </Button>
            </Link>
            <Link href="/setup">
              <Button className="bg-teal-600 hover:bg-teal-500" data-testid="button-get-started">
                Get Started
              </Button>
            </Link>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-slate-400" data-testid="button-mobile-menu" aria-label={open ? "Close menu" : "Open menu"}>
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-slate-950 border-slate-800 w-80">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-8">
                  <Link href="/" onClick={() => setOpen(false)}>
                    <img 
                      src="/assets/images/bitzo-logo.svg" 
                      alt="Bitzo" 
                      width={120} 
                      height={32} 
                      className="h-8 w-auto" 
                    />
                  </Link>
                </div>

                <nav className="flex flex-col gap-1 flex-1" data-testid="mobile-nav">
                  {navLinks.map((link) => (
                    <SheetClose asChild key={link.href}>
                      <Link href={link.href}>
                        <div
                          onClick={() => scrollToTop()}
                          className={`px-4 py-3 rounded-lg text-base font-medium transition cursor-pointer ${
                            isActive(link.href)
                              ? "bg-teal-500/10 text-teal-400"
                              : "text-slate-300 hover:bg-slate-800 hover:text-white"
                          }`}
                          data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {link.label}
                        </div>
                      </Link>
                    </SheetClose>
                  ))}
                </nav>

                <div className="border-t border-slate-800 pt-6 space-y-3">
                  <p className="text-xs text-slate-500 px-4 mb-2">Log in as:</p>
                  <SheetClose asChild>
                    <Link href="/client/login">
                      <Button variant="outline" className="w-full border-slate-700 text-slate-300 justify-start" data-testid="mobile-button-client-login">
                        Client login
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link href="/partner/login">
                      <Button variant="outline" className="w-full border-slate-700 text-slate-300 justify-start" data-testid="mobile-button-partner-login">
                        Partner login
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link href="/staff/login">
                      <Button variant="outline" className="w-full border-slate-700 text-slate-300 justify-start" data-testid="mobile-button-staff-login">
                        Staff
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link href="/setup">
                      <Button className="w-full bg-teal-600 hover:bg-teal-500" data-testid="mobile-button-get-started">
                        Get Started
                      </Button>
                    </Link>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
